import { Checkbox, StatusHint } from '@indipia/ui-core';
import {
  CUST_REQ_SHARE_MESSAGE as MSG,
  isRequestOnly,
  resolveReqEdit,
  splitShareItems,
  type CustReqShareItem,
  type ReqShareOverrides,
} from './custReqCustomerShare.logic';

/**
 * 작업보고 편집·발송 왼쪽 카드의 '고객 요청' 영역 — 기획 004 F·F-3.
 * 반영된 요청만 체크 목록(기본 켜짐), 미반영 요청은 회색 한 줄 참고(고객님께 보이지 않음).
 * 발송 완료(잠금) 건은 읽기 전용.
 */
export function CustReqShareEditSection({
  items, overrides, locked, hasShareContent, onToggle,
}: {
  items: readonly CustReqShareItem[];
  overrides: ReqShareOverrides;
  locked: boolean;
  /** 메시지 또는 선택한 사진이 있는지 — 없으면 요청만으로는 발송되지 않는다는 안내를 보임 */
  hasShareContent: boolean;
  onToggle: (item: CustReqShareItem, selected: boolean) => void;
}) {
  if (items.length === 0) return null;
  const { applied, unapplied } = splitShareItems(items);
  const selectedCount = applied.filter((it) => resolveReqEdit(it, overrides).selected).length;

  return (
    <div className="mt-3" aria-label={MSG.title}>
      <div className="mb-1 text-caption font-bold text-text-secondary">
        {MSG.title}
        <span className="ml-2 font-medium text-text-muted">{MSG.titleHint}</span>
      </div>

      {applied.length > 0 && (
        <ul className="space-y-1.5">
          {applied.map((it) => {
            const on = resolveReqEdit(it, overrides).selected;
            return (
              <li key={it.custReqId} className="list-none">
                <label
                  className={[
                    'flex items-start gap-2 rounded-md border px-3 py-2.5',
                    on ? 'border-[var(--indipia-purple-500)] bg-[var(--indipia-purple-50)]' : 'border-border bg-surface',
                    locked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-surface-muted',
                  ].join(' ')}
                >
                  <Checkbox
                    bare
                    className="mt-0.5 shrink-0"
                    checked={on}
                    disabled={locked}
                    onChange={() => onToggle(it, !on)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block whitespace-pre-line break-words text-body-sm leading-relaxed text-text">
                      {it.content}
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      {unapplied.length > 0 && (
        <ul className={applied.length > 0 ? 'mt-1.5 space-y-1' : 'space-y-1'}>
          {unapplied.map((it) => (
            <li
              key={it.custReqId}
              className="flex items-center gap-2 rounded-md bg-surface px-3 py-1.5 text-caption text-text-muted"
            >
              <span className="min-w-0 flex-1 truncate">{it.content}</span>
              <span className="shrink-0">{MSG.unappliedNote}</span>
            </li>
          ))}
        </ul>
      )}

      {!locked && isRequestOnly(selectedCount, hasShareContent) && (
        <div className="mt-1.5" role="status">
          <StatusHint tone="warning">{MSG.onlyRequestsHint}</StatusHint>
        </div>
      )}
    </div>
  );
}
